import type { PlanProjectionGroup } from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type PlanDagNode, planDagNodes } from '../lib/plan-dag';
import { StatusPill, type StatusTone } from './StatusPill';

// The Custom plans breakdown's DAG rail (#4341) — groups in `(position,
// groupId)` order (`planDagNodes` already resolves that ordering, plus the
// humanised hold text), rendered left-to-right with a hairline edge between
// consecutive nodes. Presenter only: <PlanBreakdown/> owns the fetch; this
// takes a plan's groups and shapes nothing itself. Renders only at 2+ groups
// — the caller's call, not this component's (77% of plans have exactly one).
//
// COLOUR IS NEVER THE ONLY SIGNAL (house rule + WCAG 1.4.1): each node's
// border-colour modifier is paired with a `<StatusPill>` TEXT label, so a
// colour-blind or monochrome reader still tells landed from stalled from
// in-flight — the same pairing `styles/views/_fleet.scss`'s `.plan-dag__node`
// comment calls for ("landed and stalled reuse the same hues <StatusPill>
// already carries").
//
// `fleet.plans.custom.disposition.*` is the SAME key family
// `PlanGroupTable.tsx`'s status pill reads — one vocabulary across both
// breakdown surfaces, curated over `PlanGroupDisposition`
// (`packages/domain/src/value-objects/plan-projection.ts`).
//
// ONLY TWO modifiers exist on `.plan-dag__node` for disposition itself —
// `--landed` and `--stalled` — `in_flight` is the unmodified default
// appearance, and `--held` is an INDEPENDENT overlay (dashed border) applied
// whenever the group carries an active hold, regardless of disposition (a
// held group's disposition is always `in_flight` per `PlanGroupHoldSchema`'s
// docblock — waiting to start is not the same fact as being unable to ever
// finish).

const DISPOSITION_TONE: Record<PlanDagNode['disposition'], StatusTone> = {
  landed: 'success',
  // Released like `landed`, but nothing shipped — a success in tone would claim it did.
  no_work: 'warning',
  in_flight: 'neutral',
  stalled: 'danger',
};

function dispositionLabel(disposition: PlanDagNode['disposition']): string {
  return t(`fleet.plans.custom.disposition.${disposition}`);
}

export type PlanDagRailProps = {
  readonly groups: readonly PlanProjectionGroup[];
};

export function PlanDagRail(props: PlanDagRailProps): JSX.Element {
  const nodes = (): PlanDagNode[] => planDagNodes(props.groups);

  return (
    <div class="plan-dag">
      <For each={nodes()}>
        {(node, index) => (
          <>
            <Show when={index() > 0}>
              <span class="plan-dag__edge" aria-hidden="true" />
            </Show>
            <div
              class="plan-dag__node"
              classList={{
                'plan-dag__node--landed': node.disposition === 'landed',
                'plan-dag__node--stalled': node.disposition === 'stalled',
                'plan-dag__node--held': node.hold !== null,
              }}
            >
              <span class="plan-dag__node-title">{node.title}</span>
              <StatusPill tone={DISPOSITION_TONE[node.disposition]}>
                {dispositionLabel(node.disposition)}
              </StatusPill>
              <Show when={node.hold}>
                {(hold) => (
                  <span class="plan-dag__node-hold">
                    {t(hold().labelKey, { upstream: hold().upstreamTitle ?? '' })}
                  </span>
                )}
              </Show>
            </div>
          </>
        )}
      </For>
    </div>
  );
}
